import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import KPICardWithTrend from "../KPICardWithTrend";
import styles from "./PatientOverview.module.css";
import MonthYearPicker from "../../shared-modules/DatePicker/MonthYearPicker";
import { getPatientReadingMetrics, getDeviceMetrics, getComplianceTrend } from "../../../services/super-admin-dashboard/patientOverview";
import MonthlyComplianceChart from "./MonthlyComplianceChart";
import DeviceDistributionChart from "./DeviceDistributionChart";
import { buildMonthAwareTitle, getDashboardMonthContext } from "../kpiLabelUtils";

const PatientOverview = ({ scope = "all" }) => {
  const [selectedMonthYear, setSelectedMonthYear] = useState("");
  const { hasMonthFilter, monthLabel } = getDashboardMonthContext(selectedMonthYear);

  const { data: readingMetrics, isLoading } = useQuery({
    queryKey: ["patient-overview-reading-metrics", scope, selectedMonthYear],
    queryFn: () => getPatientReadingMetrics(scope, selectedMonthYear || undefined),
    staleTime: 5 * 60 * 1000,
  });

  const { data: deviceMetrics, isLoading: isDeviceMetricsLoading } = useQuery({
    queryKey: ["patient-overview-device-metrics", scope, selectedMonthYear],
    queryFn: () => getDeviceMetrics(scope, selectedMonthYear || undefined),
    staleTime: 5 * 60 * 1000,
  });

  const { data: complianceTrend, isLoading: isComplianceLoading } = useQuery({
    queryKey: ["patient-overview-compliance-trend", scope, selectedMonthYear],
    queryFn: () => getComplianceTrend(scope, selectedMonthYear || undefined),
    staleTime: 5 * 60 * 1000,
  });

  const monthlyComplianceData = complianceTrend || [];

  const deviceStatusData = [
    {
      name: "Connected",
      value: deviceMetrics?.connected ?? 0,
      pct: deviceMetrics?.connected_pct ?? 0,
      color: "#16a249",
    },
    
    {
      name: "Delivered",
      value: deviceMetrics?.delivered ?? 0,
      pct: deviceMetrics?.delivered_pct ?? 0,
      color: "#f59f0a",
    },
    {
      name: "Unlinked",
      value: deviceMetrics?.unlinked ?? 0,
      pct: deviceMetrics?.unlinked_pct ?? 0,
      color: "#64748b",
    }
  ];

  const totalDevices = deviceMetrics?.total_devices ?? 0;

  const kpiTitles = {
    totalReadings: buildMonthAwareTitle({
      hasMonthFilter,
      monthLabel,
      defaultLabel: "Total Readings",
      
      showMonthWhenFiltered: false,
    }),
    rpmCompliance: buildMonthAwareTitle({
      hasMonthFilter,
      monthLabel,
      defaultLabel: "RPM Compliance",
      showMonthWhenUnfiltered: true,
      showMonthWhenFiltered: false,
      monthClassName: styles.monthBadge,
    }),
    ccmCompliance: buildMonthAwareTitle({
      hasMonthFilter,
      monthLabel,
      defaultLabel: "CCM Compliance",
      showMonthWhenUnfiltered: true,
      showMonthWhenFiltered: false,
      monthClassName: styles.monthBadge,
    }),
    devices: buildMonthAwareTitle({
      hasMonthFilter,
      monthLabel,
      defaultLabel: "Total Connected Devices",
      filteredLabel: "Devices Assigned",
      showMonthWhenFiltered: false,
    }),
  };

  return (
    <div className={styles.sectionCard}>
      <div className={styles.headerRow}>
        <h2 className={styles.title}>Patient Overview</h2>
         <MonthYearPicker value={selectedMonthYear} onChange={setSelectedMonthYear} />
      </div>
      <div className={styles.divider} />

      <div className={styles.kpiGrid}>
        <KPICardWithTrend
          title={kpiTitles.totalReadings}
          value={isLoading ? "..." : String(readingMetrics?.total_readings ?? 0)}
          className={styles.kpiBlue}
          isLoading={isLoading}
        />
        <KPICardWithTrend
          title={kpiTitles.rpmCompliance}
          value={isLoading ? "..." : String(readingMetrics?.rpm_compliant ?? 0)}
          className={styles.kpiBlue}
          isLoading={isLoading}
        />
        <KPICardWithTrend
          title={kpiTitles.ccmCompliance}
          value={isLoading ? "..." : String(readingMetrics?.ccm_compliant ?? 0)}
          className={styles.kpiGreen}
          isLoading={isLoading}
        />
      
        <KPICardWithTrend
          title={kpiTitles.devices}
          value={isLoading ? "..." : String(readingMetrics?.active_devices ?? 0)}
          className={styles.kpiIndigo}
          isLoading={isLoading}
        />
      </div>

      <div className={styles.chartsGrid}>
        <MonthlyComplianceChart data={monthlyComplianceData} isLoading={isComplianceLoading} />
        <DeviceDistributionChart
          data={deviceStatusData}
          totalDevices={totalDevices}
          isLoading={isDeviceMetricsLoading}
        />
      </div>
    </div>
  );
};

export default PatientOverview;
